import { Component, OnInit, ViewChild } from "@angular/core";
import { Table } from "primeng/table";
import { SupplierAnalyticsService } from "src/app/services/api/supplier-analytics.service";
import { FilterMetadata } from "primeng/api";
import { SUPPLIERANALYTICS } from "src/app/controller/constants/supplier-admin";
import { Router } from "@angular/router";
import { MatDialogRef } from "@angular/material/dialog";
import { ModalComponent } from "src/app/modal/modal.component";
import { ButtonComponent } from "src/app/components/buttons/button/button.component";
import { Task } from "src/app/tasks/task";

interface Column {
  field: string;
  header: string;
}

@Component({
  selector: "app-supplier-evaluation",
  templateUrl: "./supplier-evaluation.component.html",
  styleUrls: ["./supplier-evaluation.component.css"],
  standalone: false,
})
export class SupplierEvaluationComponent {
  @ViewChild("dt") dt: Table | undefined;

  ID = SUPPLIERANALYTICS.ID;
  PATH = SUPPLIERANALYTICS.PATH;
  title = SUPPLIERANALYTICS.TITLE;
  description = SUPPLIERANALYTICS.DESCRIPTION;
  backButtonLabel = "screen.admin.dashboard";
  navigateBackTask: Task;

  searchValue: string | undefined;

  suppliersAnalytics: SupplierAnalytics[] = [];

  years: string[] = ["2021", "2022", "2023", "2024", "2025"];
  selectedYearsDefault: string[] = ["2025", "2024", "2023"];
  selectedYears: string[] = [...this.selectedYearsDefault];

  selectedSuppliers: any[] = [];
  supplierOptions: any[] = [];

  umsatzCols: string[] = [
    "Anzahl Bestellungen",
    "Anzahl Bestellpositionen",
    "Jahresumsatz",
  ];

  rekalamtionquoteCols: string[] = [
    "Anzahl Bestellpositionen",
    "Anzahl Reklamationen",
    "Warenwert Reklamation (€)",
    "Reklamationsquote in %",
  ];

  termintreueCols: string[] = [
    "Mittlere Verspätung (Tage)",
    "Verspätete Bestellpositionen",
    "Pünktlich oder zu Früh",
    "Maximale Verspätung (Tage)",
  ];

  vorjahresVergleichPosIncCols: string[] = [
    ...this.umsatzCols,
    "Pünktlich oder zu Früh",
  ];

  vorjahresVergleichNegIncCols: string[] = [
    ...this.termintreueCols,
    ...this.rekalamtionquoteCols,
  ];

  topics: any[] = [
    {
      label: "Reklamationsquote",
      columns: this.rekalamtionquoteCols,
    },
    {
      label: "Reklamationsursachen",
      columns: ["Anzahl Reklamationen"],
    },
    {
      label: "Termintreue",
      columns: this.termintreueCols,
    },
    {
      label: "Zuverlässigkeit Auftragsbestätigungen",
      columns: [
        "Anzahl Bestellpositionen",
        "Anzahl unbestätigte Positionen",
        "Unzuverlässigkeit Überstellung Auftragsbestätigungen in %",
      ],
    },
    {
      label: "Umsatz",
      columns: this.umsatzCols,
    },
  ];

  fehlerarten: string[] = [];

  selectedTopics: any[] = JSON.parse(JSON.stringify([this.topics[0]])); // select all by default

  loading = true;

  cols: Column[] = [];
  selectedColumns: Column[] = [];

  // get cols() {
  //   let topicColumns: any[] = this.topics.map((t) => t.columns);
  //   return [].concat(...topicColumns) as string[];
  // }

  // get selectedColumns() {
  //   let topicColumns: any[] = this.selectedTopics.map((t) => t.columns);
  //   return [].concat(...topicColumns) as string[];
  // }

  constructor(
    private supplierAnalyticsService: SupplierAnalyticsService,
    private router: Router
  ) {
    this.updateColumns();

    this.navigateBackTask = new SupplierEvaluationNavigateBackTask(router);

    supplierAnalyticsService
      .getSupplierComplainsFehlerarten()
      .then((fehlerarten) => {
        this.fehlerarten = fehlerarten;
        this.topics[1].columns.push(...this.fehlerarten);
        this.vorjahresVergleichNegIncCols.push(...fehlerarten);

        for (let col of [
          ...this.umsatzCols,
          ...this.rekalamtionquoteCols,
          ...this.termintreueCols,
          ...this.fehlerarten,
        ]) {
          this.cols.push({ field: col, header: col });
        }
      });

    supplierAnalyticsService.getSupplierAnalyics().then((suppliers) => {
      if (suppliers) {
        let yearsCollection: string[] = [];
        this.suppliersAnalytics = [];
        let yearTotals: { [year: string]: { [field: string]: number } } = {}; // Sum per field per year
        let yearCounts: { [year: string]: number } = {};

        Object.keys(suppliers).forEach((supplierId) => {
          let { name, data: yearData } = (suppliers as any)[supplierId];
          this.supplierOptions.push({
            label: `${name} (${supplierId})`,
            value: supplierId,
            name: name,
          });

          let years = Object.keys(yearData);
          for (let y of years) {
            yearsCollection.push(y);
            let data: { [key: string]: number } = {};
            Object.keys(yearData[y]).forEach((dataField) => {
              if (dataField != "Fehlerarten") {
                data[dataField] = Number(yearData[y][dataField]);
              }
            });

            for (let art of this.fehlerarten) {
              if ("Fehlerarten" in yearData[y]) {
                data[art] =
                  art in yearData[y]["Fehlerarten"]
                    ? yearData[y]["Fehlerarten"][art]
                    : 0;
              } else {
                data[art] = 0;
              }
            }

            for (let uCol of [
              ...this.vorjahresVergleichPosIncCols,
              ...this.vorjahresVergleichNegIncCols,
            ]) {
              let prev_year_data = yearData[parseInt(y) - 1];
              if (prev_year_data) {
                data[uCol + "prct_vorjahr"] =
                  (-1 * (prev_year_data[uCol] - data[uCol])) /
                  prev_year_data[uCol];
              }
            }

            this.suppliersAnalytics.push({
              supplier: {
                id: supplierId,
                name: name,
              },
              year: y,
              data,
            });

            if (!yearTotals[y]) {
              yearTotals[y] = {};
              yearCounts[y] = 0;
            }
            Object.keys(data).forEach((field) => {
              if (!(field in yearTotals[y])) {
                yearTotals[y][field] = 0;
              }
              yearTotals[y][field] += data[field];
            });
            yearCounts[y]++;
          }

        });

        var yearlyAverages: {
          [year: string]: { [field: string]: number }
        } = {}
        Object.keys(yearTotals).forEach((year) => {
          yearlyAverages[year] = {};
          Object.keys(yearTotals[year]).forEach((field) => {

            if (field.includes("prct_vorjahr")) {
              
            } else {
              yearlyAverages[year][field] = yearTotals[year][field] / yearCounts[year];

              let prev_year_data = yearlyAverages[parseInt(year) - 1];
              if (prev_year_data) {
                yearlyAverages[year][field + "prct_vorjahr"] =
                  (-1 * (prev_year_data[field] - yearlyAverages[year][field])) /
                  prev_year_data[field];

              }
            }
          });

          this.suppliersAnalytics.push({
            supplier: {
              id: '0',
              name: 'Durchschnitt',
            },
            year: year,
            data: yearlyAverages[year],
          });
          
        });

        this.years = [...new Set(yearsCollection)].sort();
        (this.dt?.filters["year"] as FilterMetadata[])[0].value =
          this.selectedYears;
        // this.dt?.filter(0, 'data.Anzahl Reklamationen', 'gt');
        this.loading = false;



      }
    });
  }

  getYears(supplierData: any) {
    return Object.keys(supplierData);
  }

  updateColumns() {
    this.selectedColumns = [];
    for (let topic of this.selectedTopics) {
      for (let col of topic.columns) {
        console.log(col);
        this.selectedColumns.push({ field: col, header: col });
      }
    }
  }

  topicSelected(event: any) {
    this.selectedTopics = event.value;

    this.updateColumns();

    if (
      this.selectedTopics.length == 1 &&
      this.selectedTopics[0].label == "Termintreue"
    ) {
      this.dt?.filter(0, "data.Verspätete Bestellpositionen", "gte");
    }
  }

  applyFilterGlobal($event: any, stringVal: any) {
    this.dt!.filterGlobal(($event.target as HTMLInputElement).value, stringVal);
  }

  clear(table: Table) {
    table.clear();
    this.searchValue = "";
    this.selectedYears = [...this.selectedYearsDefault];
    this.selectedSuppliers = [];
    this.onYearsChange(this.selectedYearsDefault, undefined);
  }

  onYearsChange(event: any, filter: Function | undefined) {
    if (filter) {
      filter(event, "year", "in");
    } else {
      this.dt?.filter(event, "year", "in");
    }
  }

  onSupplierFilterChange(event: any, table: Table) {
    if (!this.selectedSuppliers || this.selectedSuppliers.length === 0) {
      table.clear(); // or use your own method to reset
    } else {
      let ids: string[] = [];
      console.log(this.selectedSuppliers);
      this.selectedSuppliers.forEach((s) => {
        ids.push(s.value);
      });
      console.log(ids);
      table.filter(ids, "supplier.id", "in");
    }
  }

  showPrevYearChangePositiveIncrease(colName: string) {
    return this.vorjahresVergleichPosIncCols.includes(colName);
  }

  showPrevYearChangeNegativeIncrease(colName: string) {
    if (
      colName == "Anzahl Bestellpositionen" ||
      colName == "Pünktlich oder zu Früh"
    ) {
      return false;
    }
    return this.vorjahresVergleichNegIncCols.includes(colName);
  }

  checkPercentageValue(val: string) {
    return !isNaN(Number(val)) && isFinite(Number(val));
  }
}

export interface SupplierAnalytics {
  supplier: {
    id: string;
    name: string;
  };
  year: string;
  data: {
    [key: string]: number;
  };
}

export class SupplierEvaluationNavigateBackTask implements Task {
  constructor(private router: Router) {}

  async execute(
    dialogRef?: MatDialogRef<ModalComponent, any> | undefined,
    buttonComponent?: ButtonComponent | undefined
  ): Promise<void> {
    this.router.navigate(["admin"]);
  }
}
